import React, { useCallback, useEffect, useState } from 'react';
import {
  Layout, Card, Row, Col, Button, Alert, Spin, Typography, Tag, Tooltip, Segmented,
} from 'antd';
import {
  ReloadOutlined, LogoutOutlined, WarningOutlined, CheckCircleOutlined, RightOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import { fetchOverview, clearToken } from './api';
import { palette, compact, money, prettyDate } from './theme';
import { StackedStrip, agingSegments, MonthlyColumns, StoreBars } from './charts';
import DetailDrawer from './DetailDrawer';
import Footer from './Footer';
import {
  SCOPE_OPTIONS, SCOPE_RBS, SCOPE_SBY, loadScope, saveScope, isBoth,
  pick, filterRows, scopedAging,
} from './scope';

const { Header, Content } = Layout;
const { Text, Title } = Typography;

// The landing screen is RANKED, not a grid of six equals. The owner opens it to
// answer "is anything wrong?" — so the money position leads, this month's
// trading comes second, and stock (which moves slowly and rarely surprises)
// comes last. Every tile is one click from the detail behind it, and no table
// appears until he asks for one.

const SECTION = { fontSize: 12, letterSpacing: 0.6, textTransform: 'uppercase',
                  color: palette.inkMuted, marginBottom: 8, fontWeight: 600 };

// The two operational systems, for jumping straight from a figure to the app
// that produced it. Overridable at build time so a domain change (the cutover
// still has rbs/sby.app.autone.lk on the table) needs no code edit.
//
// SLB points at sby.app.slb.lk and that is correct, not a typo: SBY is the
// internal name for Sealanie Boat Yard and SLB is the brand it trades under.
// Every folder, profile and script in this repo says SBY; the customer sees
// SLB. Do not "fix" one to match the other.
const SYSTEM_LINKS = [
  { label: 'RBS', url: process.env.REACT_APP_RBS_URL || 'https://rbs.app.slb.lk' },
  { label: 'SLB', url: process.env.REACT_APP_SBY_URL || 'https://sby.app.slb.lk' },
];

/**
 * The shared tile shell.
 *
 * The value is compact (Rs 116.1M) with the exact figure on hover: at a glance
 * the magnitude is the message, and counting digits is work. The per-company
 * split sits directly under it because these two companies are different
 * businesses and the group total alone hides that.
 */
function Tile({ label, amount, split, status, children, onOpen, qualifier, footnote }) {
  return (
    <Card
      hoverable={!!onOpen}
      onClick={onOpen}
      styles={{ body: { padding: 16 } }}
      style={{ height: '100%', cursor: onOpen ? 'pointer' : 'default' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Text style={{ color: palette.inkSecondary, fontSize: 13 }}>{label}</Text>
        {onOpen && <RightOutlined style={{ fontSize: 11, color: palette.inkMuted }} />}
      </div>

      {/* Only set where the headline figure is gross or net of something the
          neighbouring tile treats differently (see §8.4 below) — most tiles
          leave this out. */}
      {qualifier && (
        <div style={{ fontSize: 11, color: palette.inkMuted, marginTop: 2 }}>{qualifier}</div>
      )}

      <Tooltip title={money(amount)}>
        <div style={{ fontSize: 30, fontWeight: 600, color: palette.ink,
                      lineHeight: 1.25, margin: '4px 0 8px' }}>
          {compact(amount)}
        </div>
      </Tooltip>

      {/* Callers pass split=null under a single-company scope: restating the
          headline beside a company that is not being shown reads as a
          contradiction. */}
      {split && (
        <div style={{ display: 'flex', gap: 16, fontSize: 12,
                      color: palette.inkSecondary, marginBottom: 10 }}>
          <Tooltip title={money(split.rbs)}>
            <span>RBS <b style={{ color: palette.ink }}>{compact(split.rbs)}</b></span>
          </Tooltip>
          <Tooltip title={money(split.sby)}>
            <span>SBY <b style={{ color: palette.ink }}>{compact(split.sby)}</b></span>
          </Tooltip>
        </div>
      )}

      {children}
      {status}

      {/* Reconciliation aid, not decoration. Where a tile's headline is
          deliberately gross or net of something, this is where the OTHER figure
          goes, named after the tenant report that shows it — so someone holding
          the two side by side finds the match on screen instead of filing a
          bug. See the Customer Outstanding caller. */}
      {footnote && (
        <div style={{ fontSize: 11, color: palette.inkMuted, marginTop: 8,
                      paddingTop: 8, borderTop: `1px solid ${palette.gridline}` }}>
          {footnote}
        </div>
      )}
    </Card>
  );
}

/**
 * The status line. Colour never carries the meaning on its own — there is
 * always an icon and a sentence, because a colour-blind reader and a
 * black-and-white printout must both get the same message.
 */
function Status({ level, children }) {
  const colour = level === 'warning' ? palette.warning
    : level === 'critical' ? palette.critical : palette.good;
  const Icon = level === 'good' ? CheckCircleOutlined : WarningOutlined;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 12,
                  color: level === 'good' ? palette.inkSecondary : palette.ink }}>
      <Icon style={{ color: colour }} />
      <span>{children}</span>
    </div>
  );
}

// One line per inventory account, no fixed Raw Materials/WIP/Finished Goods
// taxonomy — each account keeps its own name from the chart of accounts
// (see TenantQueries.inventoryBreakdown / InventoryLine). Every normal line
// below is that account's general-ledger balance (POSTED, non-reversal
// entries as of today) — which is what makes the total reconcile to the
// client's Trial Balance to the cent, and why there is no item-level detail
// (no item, no quantity) behind any of these figures, unlike the per-store
// stock drill-down. Two things are called out explicitly rather than left
// for the reader to notice:
//   - a negative Work in Progress line, which is impossible in reality and
//     always means an unreversed duplicate posting, not ordinary variance;
//   - stock tagged to an account that turns out not to be an inventory
//     account at all — shown from STOCK RECORDS instead of that account's
//     ledger balance, because the ledger balance is a different, unrelated
//     fact (e.g. an equity account's normal equity activity) and showing it
//     here would misrepresent it as inventory.
function InventoryBreakdownList({ lines, showCompany }) {
  const normal = lines.filter((l) => !l.anomaly);
  const anomalies = lines.filter((l) => l.anomaly);
  const hasNegative = normal.some((l) => l.negative);

  if (!lines.length) {
    return <div style={{ color: palette.inkMuted, fontSize: 13 }}>No inventory accounts found.</div>;
  }

  return (
    <>
      <div style={{ fontSize: 11, color: palette.inkMuted, marginBottom: 4 }}>
        Each account's general ledger balance — reconciles to the Trial Balance.
      </div>
      {normal.map((l) => (
        <div key={`${l.company}-${l.accountCode}-${l.accountName}`} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '7px 0', borderBottom: `1px solid ${palette.gridline}`,
        }}>
          <div>
            <span style={{ fontWeight: 500 }}>
              {showCompany ? `${l.company} — ` : ''}{l.accountName}
            </span>
            {l.negative && (
              <WarningOutlined style={{ color: palette.critical, marginLeft: 6, fontSize: 12 }} />
            )}
          </div>
          <Tooltip title={money(l.value)}>
            <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                          color: l.negative ? palette.critical : palette.ink }}>
              {compact(l.value)}
            </span>
          </Tooltip>
        </div>
      ))}

      {hasNegative && (
        <Status level="critical">
          Negative Work in Progress is impossible in reality — a known unreversed duplicate closing
          entry, not ordinary variance
        </Status>
      )}

      {anomalies.length > 0 && (
        <Alert
          style={{ marginTop: 12 }}
          type="warning"
          showIcon
          message="Stock tagged to a non-inventory account — excluded from Total inventory"
          description={
            <div>
              <div style={{ marginBottom: 4 }}>
                From stock records, not this account's ledger balance — that balance reflects
                whatever else the account is normally used for, and showing it here would
                misrepresent unrelated activity as inventory.
              </div>
              {anomalies.map((l) => (
                <div key={`${l.company}-${l.accountCode}-${l.accountName}`} style={{
                  display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 12,
                }}>
                  <span>
                    {showCompany ? `${l.company} — ` : ''}{l.accountName} ({l.accountType} account)
                  </span>
                  <b>{money(l.value)}</b>
                </div>
              ))}
            </div>
          }
        />
      )}
    </>
  );
}

/** Aging strip plus the one sentence that says whether it matters. */
function AgingTile({ label, amount, split, rbsAging, sbyAging, scope, overdueWord, onOpen,
                     qualifier, footnote }) {
  const aging = scopedAging(rbsAging, sbyAging, scope);
  const overdue = Number(aging?.overdue60Plus ?? 0);
  const total = Number(amount ?? 0);
  const share = total > 0 ? overdue / total : 0;

  // Thresholds are on the SHARE, not the amount: Rs 5M overdue means something
  // very different against a Rs 6M book than against a Rs 90M one.
  const level = share >= 0.4 ? 'critical' : share >= 0.15 ? 'warning' : 'good';

  const segments = agingSegments(aging);

  return (
    <Tile label={label} amount={amount} split={split} onOpen={onOpen} qualifier={qualifier}
      footnote={footnote}
      status={
        overdue > 0
          ? <Status level={level}>
              {compact(overdue)} {overdueWord} over 60 days
            </Status>
          : <Status level="good">Nothing over 60 days</Status>
      }
    >
      <StackedStrip segments={segments} ariaLabel={`${label} by age`} />
    </Tile>
  );
}

export default function Dashboard({ onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState(null);
  const [scope, setScope] = useState(loadScope);

  const changeScope = (next) => { setScope(next); saveScope(next); };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchOverview());
    } catch (e) {
      setError(e.message);
      // A 401 means the token is already cleared by the api layer; bounce to
      // login rather than leaving an empty dashboard with an error on it.
      if (e.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const signOut = () => { clearToken(); onLogout(); };

  const sales = data?.sales;
  const rbsSales = sales?.rbs;
  const sbySales = sales?.sby;

  // The group's biggest customer, whichever company it belongs to. Reading only
  // RBS would be right today and quietly wrong the first month SBY invoices
  // anyone.
  const topCustomer = (() => {
    const candidates = [
      scope !== SCOPE_SBY && rbsSales?.topCustomer
        && { ...rbsSales.topCustomer, company: 'RBS' },
      scope !== SCOPE_RBS && sbySales?.topCustomer
        && { ...sbySales.topCustomer, company: 'SBY' },
    ].filter(Boolean);
    if (candidates.length === 0) return null;
    return candidates.reduce((a, b) => (Number(b.amount) > Number(a.amount) ? b : a));
  })();

  // The comparison base has to move with the scope too. Comparing a scoped
  // "this month" against a group "last month" would show RBS down 90% the
  // moment someone switched away from Both.
  const monthDelta = (() => {
    if (!sales) return null;
    const now = Number(pick(sales.thisMonthNet, scope) ?? 0);
    const prev = scope === SCOPE_RBS ? Number(rbsSales?.previousNet ?? 0)
      : scope === SCOPE_SBY ? Number(sbySales?.previousNet ?? 0)
      : Number(rbsSales?.previousNet ?? 0) + Number(sbySales?.previousNet ?? 0);
    if (prev === 0) return null;
    return ((now - prev) / prev) * 100;
  })();

  const todayNet = scope === SCOPE_RBS ? Number(rbsSales?.todayNet ?? 0)
    : scope === SCOPE_SBY ? Number(sbySales?.todayNet ?? 0)
    : Number(rbsSales?.todayNet ?? 0) + Number(sbySales?.todayNet ?? 0);

  // The scoped company's own sales block, or null under Both. Used to decide
  // whether the headline can honestly be a number.
  const scopedSales = scope === SCOPE_RBS ? rbsSales : scope === SCOPE_SBY ? sbySales : null;

  // Customer Outstanding is deliberately GROSS (netting it reads as a large
  // negative for SBY, which is unreadable as a headline), but the tenant's own
  // Sales Outstanding Aging report totals NET of advances. On SBY that is
  // 48.6M on the tile against -132.8M on the report, off the same data — which
  // looks like a Rs 181M error to anyone comparing them. Verified 2026-09-26:
  // receivables minus advancesHeld equals that report's total to the cent on
  // both tenants, so this is the reconciling figure and it belongs on the tile.
  const netOfAdvances = Number(pick(data?.receivables, scope) ?? 0)
    - Number(pick(data?.advancesHeld, scope) ?? 0);

  const bankAccounts = filterRows(data?.bankAccounts, scope);
  const stockStores = filterRows(data?.stock?.stores, scope);
  const stockBreakdown = filterRows(data?.stock?.breakdown, scope);
  const splitOf = (amount) => (isBoth(scope) ? amount : null);

  return (
    <Layout style={{ minHeight: '100vh', background: palette.plane }}>
      {/* height:auto + wrap: the header now carries a title, a three-way scope
          control and two buttons, which do not fit across a phone. antd's
          Header is a fixed 64px with a matching line-height, so both have to be
          released or the row silently overlaps itself instead of wrapping. */}
      <Header style={{ background: palette.surface, display: 'flex', alignItems: 'center',
                       justifyContent: 'space-between', padding: '10px 16px',
                       height: 'auto', lineHeight: 1.4, gap: 8, flexWrap: 'wrap',
                       borderBottom: `1px solid ${palette.gridline}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
          <Title level={4} style={{ margin: 0, whiteSpace: 'nowrap' }}>SLB Dashboard</Title>
          {/* Purely a view filter — everything it switches between is already in
              the payload, so it never refetches and the scoped and combined
              figures cannot disagree. */}
          <Segmented options={SCOPE_OPTIONS} value={scope} onChange={changeScope} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* Deliberately separated from the scope control on the left: both
              carry the label "RBS", and one filters this page while the other
              leaves it. The icon and the "Open" prefix are what tell them
              apart. */}
          <Text style={{ fontSize: 12, color: palette.inkMuted }}>Open</Text>
          {SYSTEM_LINKS.map(({ label, url }) => (
            <Button key={label} type="link" size="small" href={url}
                    target="_blank" rel="noopener noreferrer"
                    icon={<ExportOutlined />} style={{ padding: '0 4px' }}>
              {label}
            </Button>
          ))}
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          <Button icon={<LogoutOutlined />} onClick={signOut}>Sign out</Button>
        </div>
      </Header>

      <Content style={{ padding: 16, maxWidth: 1400, width: '100%', margin: '0 auto' }}>
        {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

        {data && (
          <div style={{ marginBottom: 16 }}>
            {/* Timezone is surfaced deliberately: the server runs UTC while the
                business runs Asia/Colombo, and a wrong "today" should be
                diagnosable from the screen rather than a mystery. */}
            <Tag>As of {prettyDate(data.asOfDate)}</Tag>
            <Tag>{data.timezone}</Tag>
          </div>
        )}

        {loading && !data ? (
          <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>
        ) : data && (
          <>
            <div style={SECTION}>Money position</div>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} xl={6}>
                <Tile
                  label="Cash Flows"
                  amount={pick(data.cash, scope)}
                  split={splitOf(data.cash)}
                  onOpen={() => setView('cash')}
                  status={<Status level="good">
                    {bankAccounts.length} accounts (bank + cash in hand + petty cash)
                  </Status>}
                />
              </Col>
              <Col xs={24} sm={12} xl={6}>
                {/* AP nets off supplier advances (gross would overstate RBS
                    ninefold — see analysis §8.4); AR does not, because netting
                    it produces a negative, unreadable figure for SBY (§1). The
                    two tiles look symmetrical but answer different questions,
                    so both — and the advances tile itself — carry a caption
                    naming which one they are. */}
                <AgingTile
                  label="Customer Outstanding"
                  amount={pick(data.receivables, scope)}
                  split={splitOf(data.receivables)}
                  rbsAging={data.receivablesRbs}
                  sbyAging={data.receivablesSby}
                  scope={scope}
                  overdueWord="unpaid"
                  onOpen={() => setView('receivables')}
                  qualifier="before advances held"
                  footnote={
                    <Tooltip title={money(netOfAdvances)}>
                      <span>
                        {compact(netOfAdvances)} after advances —{' '}
                        the total the tenant&rsquo;s Sales Outstanding Aging report shows
                      </span>
                    </Tooltip>
                  }
                />
              </Col>
              <Col xs={24} sm={12} xl={6}>
                <AgingTile
                  label="Supplier Outstanding"
                  amount={pick(data.payables, scope)}
                  split={splitOf(data.payables)}
                  rbsAging={data.payablesRbs}
                  sbyAging={data.payablesSby}
                  scope={scope}
                  overdueWord="unpaid"
                  onOpen={() => setView('payables')}
                  qualifier="after supplier advances"
                />
              </Col>
              <Col xs={24} sm={12} xl={6}>
                <Tile
                  label="Advances held from customers"
                  amount={pick(data.advancesHeld, scope)}
                  split={splitOf(data.advancesHeld)}
                  onOpen={() => setView('advances')}
                  qualifier="not deducted from Customer Outstanding"
                  status={<Status level="good">
                    Work owed, not cash earned
                  </Status>}
                />
              </Col>
            </Row>

            <div style={SECTION}>This month</div>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} lg={14}>
                <Card styles={{ body: { padding: 16 } }} style={{ height: '100%' }}>
                  <Text style={{ color: palette.inkSecondary, fontSize: 13 }}>
                    Net sales, {isBoth(scope) ? 'group' : scope}
                  </Text>

                  {/* Under Both, the two per-company lines below explain a zero.
                      Under a single company they are hidden, so the headline has
                      to carry the explanation itself — otherwise SBY reads as a
                      bare "Rs 0", which is the exact thing this screen was
                      supposed to stop doing. */}
                  {scopedSales && !scopedSales.hasInvoicesThisMonth ? (
                    <>
                      <div style={{ fontSize: 26, fontWeight: 600, lineHeight: 1.25,
                                    margin: '4px 0 2px', color: palette.inkSecondary }}>
                        No invoices this month
                      </div>
                      <div style={{ fontSize: 12, color: palette.inkMuted, marginBottom: 12 }}>
                        last invoice {prettyDate(scopedSales.lastInvoiceDate)}
                      </div>
                    </>
                  ) : (
                    <>
                      <Tooltip title={money(pick(sales.thisMonthNet, scope))}>
                        <div style={{ fontSize: 30, fontWeight: 600, lineHeight: 1.25,
                                      margin: '4px 0 2px' }}>
                          {compact(pick(sales.thisMonthNet, scope))}
                        </div>
                      </Tooltip>
                      <div style={{ fontSize: 12, color: palette.inkSecondary,
                                    marginBottom: 12 }}>
                        month to date
                        {monthDelta !== null && (
                          <> · <span style={{
                            color: monthDelta >= 0 ? palette.good : palette.critical }}>
                            {monthDelta >= 0 ? '▲' : '▼'} {Math.abs(monthDelta).toFixed(1)}%
                          </span> vs last month</>
                        )}
                        {' · '}today {compact(todayNet)}
                      </div>
                    </>
                  )}

                  <MonthlyColumns points={sales.months} scope={scope} />

                  {/* Only worth showing when both are in scope — otherwise the
                      single line just repeats the headline above it. */}
                  {isBoth(scope) && (
                    <Row gutter={16} style={{ marginTop: 16 }}>
                      <Col span={12}><CompanySalesLine s={rbsSales} name="RBS" /></Col>
                      <Col span={12}><CompanySalesLine s={sbySales} name="SBY" /></Col>
                    </Row>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={10}>
                <Card
                  hoverable
                  onClick={() => setView('customers')}
                  styles={{ body: { padding: 16 } }}
                  style={{ height: '100%', cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                                alignItems: 'baseline' }}>
                    <Text style={{ color: palette.inkSecondary, fontSize: 13 }}>
                      Biggest customer this month
                    </Text>
                    <RightOutlined style={{ fontSize: 11, color: palette.inkMuted }} />
                  </div>
                  {topCustomer ? (
                    <>
                      <div style={{ fontSize: 20, fontWeight: 600, marginTop: 6 }}>
                        {topCustomer.name}
                      </div>
                      <Tooltip title={money(topCustomer.amount)}>
                        <div style={{ fontSize: 26, fontWeight: 600, margin: '4px 0' }}>
                          {compact(topCustomer.amount)}
                        </div>
                      </Tooltip>
                      <div style={{ fontSize: 12, color: palette.inkSecondary }}>
                        {topCustomer.invoiceCount} invoices ·{' '}
                        {Number(topCustomer.shareOfMonth).toFixed(1)}% of {topCustomer.company}{' '}
                        sales
                      </div>
                      {/* Computed, not asserted. A hardcoded "nothing to worry
                          about" is a check that cannot fail, which is worse
                          than no check — this one goes amber on its own. */}
                      {Number(topCustomer.shareOfMonth) >= 30 ? (
                        <Status level="warning">
                          One customer is {Number(topCustomer.shareOfMonth).toFixed(0)}% of the
                          month
                        </Status>
                      ) : (
                        <Status level="good">No customer above 30% of the month</Status>
                      )}
                    </>
                  ) : (
                    <div style={{ color: palette.inkMuted, marginTop: 12 }}>
                      No invoices raised this month
                    </div>
                  )}
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} xl={6}>
                {/* Production completions typed EFFICIENCY_GAIN are recorded by
                    the tenant app at zero cost on purpose (no raw material is
                    consumed) — see TenantQueries.efficiencyGainValue. This
                    figure values that extra output at the item's own normal
                    production cost instead, so the tile actually moves. */}
                <Tile
                  label="Efficiency gain"
                  amount={pick(data.efficiencyGainThisMonth, scope)}
                  split={splitOf(data.efficiencyGainThisMonth)}
                  onOpen={() => setView('efficiencyGain')}
                  status={<Status level="good">
                    Valued at the item's own normal production cost
                  </Status>}
                />
              </Col>
              <Col xs={24} sm={12} xl={6}>
                <Tile
                  label="Waste"
                  amount={pick(data.wasteThisMonth, scope)}
                  split={splitOf(data.wasteThisMonth)}
                  onOpen={() => setView('waste')}
                  status={<Status level="good">Excludes cancelled wastages</Status>}
                />
              </Col>
            </Row>

            <div style={SECTION}>Stock on hand</div>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={10}>
                {/* Every line is the account's own general-ledger balance —
                    which is what makes this reconcile to the Trial Balance
                    to the cent (verified 2026-09-21). stock_batch is used
                    only to discover which accounts belong here (so a
                    ledger-sourced tile cannot miss one that currently holds
                    no batches), never to value them. Stock tagged to a
                    non-inventory account (see the breakdown card) is
                    excluded here on purpose — that account's ledger balance
                    is a different, unrelated fact, and folding it in would
                    both misstate this figure and hide exactly the problem
                    the breakdown exists to surface. */}
                <Tile
                  label="Total inventory"
                  amount={pick(data.stock.total, scope)}
                  split={splitOf(data.stock.total)}
                  onOpen={() => setView('stock')}
                  qualifier="Ledger balances of Raw Materials / WIP / Finished Goods accounts — ties to the Trial Balance"
                  status={
                    <>
                      <Status level="good">
                        {compact(pick(data.stock.defaultStore, scope))} of it in the DEFAULT store
                      </Status>
                      {Number(pick(data.stock.anomalyTotal, scope) ?? 0) !== 0 && (
                        <Status level="warning">
                          Plus {compact(pick(data.stock.anomalyTotal, scope))} tagged to a
                          non-inventory account, not counted above — see breakdown
                        </Status>
                      )}
                    </>
                  }
                />
              </Col>
              <Col xs={24} lg={14}>
                <Card styles={{ body: { padding: 16 } }} style={{ height: '100%' }}>
                  <Text style={{ color: palette.inkSecondary, fontSize: 13 }}>By store</Text>
                  <div style={{ marginTop: 12 }}>
                    <StoreBars stores={stockStores} />
                  </div>
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Card styles={{ body: { padding: 16 } }}>
                  <Text style={{ color: palette.inkSecondary, fontSize: 13 }}>By account</Text>
                  <div style={{ marginTop: 8 }}>
                    <InventoryBreakdownList lines={stockBreakdown} showCompany={isBoth(scope)} />
                  </div>
                </Card>
              </Col>
            </Row>
          </>
        )}

        <Footer />
      </Content>

      {/* Keyed on `view` so each drawer gets its own component instance. The
          drill-downs have different row shapes, and a shared instance carries
          the previous one's rows into the next one's columns. */}
      <DetailDrawer
        key={view}
        view={view}
        overview={data}
        months={sales?.months}
        scope={scope}
        onClose={() => setView(null)}
      />
    </Layout>
  );
}

/**
 * Per-company sales line.
 *
 * When a company has not invoiced this month it says so and gives the date of
 * its last invoice, instead of rendering Rs 0.00. SBY has raised two invoices
 * in its history and bills its work through customer advances; a zero beside
 * RBS's Rs 15M reads as a bug, and "last invoice 10 Apr 2026" reads as the
 * truth.
 */
function CompanySalesLine({ s, name }) {
  if (!s) return null;
  return (
    <div>
      <Text style={{ fontSize: 12, color: palette.inkMuted }}>{name}</Text>
      {s.hasInvoicesThisMonth ? (
        <>
          <Tooltip title={money(s.net)}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{compact(s.net)}</div>
          </Tooltip>
          <div style={{ fontSize: 12, color: palette.inkSecondary }}>
            gross {compact(s.gross)} · returns {compact(s.returns)}
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 15, fontWeight: 500, color: palette.inkSecondary }}>
            No invoices this month
          </div>
          <div style={{ fontSize: 12, color: palette.inkMuted }}>
            last invoice {prettyDate(s.lastInvoiceDate)}
          </div>
        </>
      )}
    </div>
  );
}
